import {Observable} from 'rxjs';
import {inject, Injectable} from '@angular/core';
import {HttpContext} from '@angular/common/http';
import {
  EditNotificationPreferencesValue,
  EditPatronValue,
  ExtendedPatronNotificationPreferenceValue,
  NotificationPreference,
  Patron,
  PatronAccountStatus,
  PatronNotificationPreferences,
  PatronNotificationPreferenceType,
  PatronReferenceOptions,
  PatronRelationships,
  PatronSettingsQuickTogglesValue,
  RelatedPatron
} from './patron.types';
import {isPopulatedArray, isPositiveNumber} from '@aviary/store/common/typing.helpers';
import {FALLBACK_NOTIFICATION_PREFERENCE} from '@store/patron/patron.fallback';
import {ApiService} from '@aviary/services/api.service';
import {
  EmailVerificationPayload,
  EmailVerificationRequest,
  NotificationPreferencePayload,
  PatronNotificationPreferencesPayload,
  PatronPinChangePayload,
  PatronUpdatePayload,
  PinLoginPayload
} from '@store/patron/patron.api-types';
import {
  contextWithErrorHandlerForChangePatronEmail,
  contextWithErrorHandlerForLoadAccountStatus,
  contextWithErrorHandlerForLoadAuthPatron,
  contextWithErrorHandlerForLoadPatronReferenceOptions,
  contextWithErrorHandlerForResendPatronEmailChangeVerification,
  contextWithErrorHandlerForVerifyPatronEmailChange,
  contextWithErrorHandlerForUpdateNotificationPreferences,
  contextWithErrorHandlerForUpdatePatron
} from '@store/patron/patron.handlers';
import {ListData} from '@aviary/store/common/common.types';
import {Checkout} from '@store/checkouts/checkouts.types';
import {Hold, ON_HOLD_HOLD_STATUSES, ReadyForPickupHolds} from '@store/holds/holds.types';
import {HttpParams} from '@angular/common/http';
import {buildQueryParamsFrom} from '@aviary/store/common/list-data.helpers';
import {Params} from '@angular/router';
import {PatronBalanceTransaction} from '@store/balance/balance.types';
import {BYPASS_400_ERROR} from '@aviary/store/http-loading/http-loading.types';

@Injectable({providedIn: 'root'})
export class PatronService {

  private readonly apiService: ApiService = inject(ApiService);

  public loadAuthPatron(): Observable<Patron> {
    const endpoint: string = 'patrons/v5/patron';

    return this.apiService.getSingleObject(endpoint, {context: contextWithErrorHandlerForLoadAuthPatron()});
  }

  public lookupAccount(cardNumber: string, pin: string): Observable<{ patron: Patron, loginToken: string }> {
    const endpoint: string = 'public/v1/patrons/login';
    const body: PinLoginPayload = {barcode: cardNumber, pin};

    return this.apiService.postAndReturnSingleObject(endpoint, body);
  }

  public loadAccountStatus(): Observable<PatronAccountStatus> {
    const endpoint: string = 'patrons/v2/patrons/account/status';

    return this.apiService.getSingleObject(endpoint, {context: contextWithErrorHandlerForLoadAccountStatus()});
  }

  public loadPatronReferenceOptions(): Observable<PatronReferenceOptions> {
    const endpoint: string = 'patrons/v5/patrons/reference-options';

    return this.apiService.getSingleObject(endpoint, {context: contextWithErrorHandlerForLoadPatronReferenceOptions()});
  }

  public changePatronEmail(email: string): Observable<string> {
    return this.requestPatronEmailChange(email, contextWithErrorHandlerForChangePatronEmail());
  }

  public resendPatronEmailChangeVerification(email: string): Observable<string> {
    return this.requestPatronEmailChange(email, contextWithErrorHandlerForResendPatronEmailChangeVerification());
  }

  public verifyPatronEmailChange(code: string, verificationUUID: string): Observable<void> {
    const endpoint: string = 'public/v1/patrons/verify-email-change';
    const body: EmailVerificationPayload = {code, verificationUUID};

    return this.apiService.postAndReturnNothing(endpoint, body, {context: contextWithErrorHandlerForVerifyPatronEmailChange()});
  }

  public changePin(currentPin: string, newPin: string): Observable<void> {
    const endpoint: string = 'patrons/v6/patrons/account/pin';
    const body: PatronPinChangePayload = {currentPin, newPin};
    const context: HttpContext = new HttpContext().set(BYPASS_400_ERROR, true);

    return this.apiService.patch(endpoint, body, {context});
  }

  public updatePatron(formValue: EditPatronValue): Observable<Patron> {
    const endpoint: string = 'patrons/v3/patrons';

    const body: PatronUpdatePayload = {
      ...formValue,
      pronounId: formValue.pronounId ?? null
    };

    return this.apiService.putAndReturnSingleObject(endpoint, body, {context: contextWithErrorHandlerForUpdatePatron()});
  }

  public updatePatronQuickToggles(value: PatronSettingsQuickTogglesValue, currentProfileValue: EditPatronValue): Observable<Patron> {
    const endpoint: string = 'patrons/v3/patrons';

    const body: PatronUpdatePayload = {
      ...currentProfileValue,
      optInReadingHistory: value.optInReadingHistory,
      oneClickHoldsEnabled: value.oneClickHoldsEnabled,
      pronounId: isPositiveNumber(currentProfileValue.pronounId) ? currentProfileValue.pronounId : null
    };

    return this.apiService.putAndReturnSingleObject(endpoint, body);
  }

  public loadNotificationPreferences(): Observable<PatronNotificationPreferences> {
    const endpoint: string = 'patrons/v2/patrons/communication-preferences';

    return this.apiService.getSingleObject(endpoint);
  }

  public loadPatronRelationships(): Observable<PatronRelationships> {
    const endpoint: string = 'patrons/v1/patrons/account/relationships';
    return this.apiService.getSingleObject(endpoint);
  }

  public loadDependentDetails(dependentId: number): Observable<RelatedPatron> {
    const endpoint: string = `patrons/v1/patrons/account/dependents/${dependentId}`;
    return this.apiService.getSingleObject(endpoint);
  }

  public loadDependentCheckouts(dependentId: number, queryParams: Params): Observable<ListData<Checkout>> {
    const endpoint: string = `patrons/v1/patrons/account/dependents/${dependentId}/checkouts`;
    const params: HttpParams = buildQueryParamsFrom(queryParams);
    return this.apiService.getListData(endpoint, {params});
  }

  public loadDependentReadyForPickupHolds(dependentId: number): Observable<ReadyForPickupHolds> {
    const endpoint: string = `patrons/v1/patrons/account/dependents/${dependentId}/holds/ready-for-pickup`;
    return this.apiService.getSingleObject(endpoint);
  }

  public loadDependentOnHoldHolds(dependentId: number, queryParams: Params): Observable<ListData<Hold>> {
    const endpoint: string = `patrons/v1/patrons/account/dependents/${dependentId}/holds`;
    const params: HttpParams = buildQueryParamsFrom({
      ...queryParams,
      statuses: ON_HOLD_HOLD_STATUSES
    });
    return this.apiService.getListData(endpoint, {params});
  }

  public loadDependentAccountStatus(dependentId: number): Observable<PatronAccountStatus> {
    const endpoint: string = `patrons/v1/patrons/account/dependents/${dependentId}/account/status`;
    return this.apiService.getSingleObject(endpoint, {context: contextWithErrorHandlerForLoadAccountStatus()});
  }

  public loadDependentBalanceTransactions(dependentId: number, queryParams: Params, includeAllTransactions: boolean):
    Observable<ListData<PatronBalanceTransaction>> {
    const endpoint: string = `patrons/v1/patrons/account/dependents/${dependentId}/account/transactions`;
    const params: HttpParams = buildQueryParamsFrom({
      ...queryParams,
      onlyOutstanding: !includeAllTransactions
    });
    return this.apiService.getListData(endpoint, {params});
  }

  public updateNotificationPreferences(
    value: EditNotificationPreferencesValue, oldPreferences: NotificationPreference[], patronId: number
  ): Observable<PatronNotificationPreferences> {
    const endpoint: string = 'patrons/v2/patrons/communication-preferences';

    const body: PatronNotificationPreferencesPayload = {
      notificationEmail: value.notificationEmail,
      phoneSms: value.phoneSms,
      smsTermsAccepted: value.smsTermsAccepted,
      notifications: this.applyNotificationPreferences(value, oldPreferences, patronId)
    };

    return this.apiService.putAndReturnSingleObject(endpoint, body, {context: contextWithErrorHandlerForUpdateNotificationPreferences()});
  }

  private applyNotificationPreferences(
    data: EditNotificationPreferencesValue, oldPreferences: NotificationPreference[], patronId: number
  ): NotificationPreferencePayload[] {
    const preferences: NotificationPreferencePayload[] = [];

    if (isPopulatedArray(oldPreferences)) {
      preferences.push(...oldPreferences);
    }

    this.applyNotificationPreference(preferences, 'ACCOUNT', data.accountNotifications, patronId);
    this.applyNotificationPreference(preferences, 'RECEIPT', data.receiptNotifications, patronId);
    this.applyNotificationPreference(preferences, 'AUTO_RENEW', data.renewNotifications, patronId);
    this.applyNotificationPreference(preferences, 'CARD_EXPIRE', data.expirationNotifications, patronId);
    this.applyNotificationPreference(preferences, 'HOLD', data.holdNotifications, patronId);
    this.applyNotificationPreference(preferences, 'NOTICE', data.noticeNotifications, patronId);
    this.applyNotificationPreference(preferences, 'ITEM_REQUEST', data.itemRequestNotifications, patronId);

    return this.convertArrayObjectsFalsyValuesToNull(preferences);
  }

  private applyNotificationPreference(
    preferences: NotificationPreferencePayload[], type: PatronNotificationPreferenceType, value: ExtendedPatronNotificationPreferenceValue, patronId: number
  ): void {
    const newPreference: Partial<NotificationPreferencePayload> = {
      patronId,
      notificationType: type,
      active: true,
      sendNotification: true,
      notifyEmail: value === 'Email',
      notifyText: value === 'SMS',
      notifyPhone: value === 'Phone'
    };

    const index: number = preferences.findIndex(item => item.notificationType === type);

    if (index < 0) {
      preferences.push({
        ...FALLBACK_NOTIFICATION_PREFERENCE,
        ...newPreference
      });
    } else {
      preferences[index] = {
        ...preferences[index],
        ...newPreference
      };
    }
  }

  private convertArrayObjectsFalsyValuesToNull<ObjectType extends object>(array: ObjectType[] = []): ObjectType[] {
    return array.map(object => this.convertFalsyValuesToNull(object));
  }

  private convertFalsyValuesToNull<ObjectType extends object>(object?: ObjectType): ObjectType {
    if (!object) return {} as ObjectType;

    return Object.entries(object).reduce(
      (accumulator, [key, value]) => ({
        ...accumulator,
        [key]: value || null
      }),
      {} as ObjectType
    );
  }

  private requestPatronEmailChange(email: string, context: HttpContext): Observable<string> {
    const endpoint: string = 'patrons/v2/patrons/change-email';
    const body: EmailVerificationRequest = {email};

    return this.apiService.postAndReturnSingleObject<string, EmailVerificationRequest>(endpoint, body, {context});
  }
}
